import React, { useState, useEffect, useContext, useRef } from 'react'
import {
  StyleSheet,
  ScrollView,
  View,
  Text,
} from 'react-native'
import { Company, Dictionary, Indicator, SeoLanguage } from '@ecountabl/lib'
import api from '../api-client'
import { ThemeContext } from '../utility/context/ThemeContext'
import ScoreExplanationContent from '../score-explanation-content'
import ProfileContent from './content'
import Header from './header'
import Logo from '../utility/logo'
import Leadership from './leadership'
import Certifications from './certifications'
import { MAX_CONTENT_WIDTH } from '../config'
import { textStyles } from '../style'
import { CustomActionSheet, CustomActionSheetRef } from '../utility/CustomActionSheet'
import ThemedDialog from '../style/dialog'
import { Ionicons } from '@expo/vector-icons'
import UserCompanyFeedbackForm from './user-feedback-form'
import { useAppSelector } from '../store'
import Haptics from '../utility/haptics'
import { log } from '../utility'
import { CompanyProfileSkeleton } from './company-profile-skeleton'

export type CompanyProfileRouteParams = {
  id?: string           // app routes from ../link.tsx
  company?: Company     // both app and nextjs (sometimes)
  indicator?: Indicator // used for share images and seo-specific features
  seo?: SeoLanguage
  params?: string[]     // from nextjs [...params.tsx]
  source?: (
    "search" | "alt" | "featured" | "recently-added" | "explore" | "transaction" |
    "corporate parent" | "owned company" | "sibling company"
  )
  index?: number // if source is a list then index should be provided
  sourceCompanyId?: string
  sourceCompanyName?: string
  showAlternatives?: boolean
  tag?: string
  searchTerm?: string
  parentCompanyId?: string
  parentCompanyName?: string
}

type CompanyProfileProps = {
  route: {
    params: CompanyProfileRouteParams
  }
}

const sortUserValues = (userValues: Dictionary<number>, sortedUserValues: string[]): string[] => {
  sortedUserValues.sort((a, b) => {
    if (userValues[a] === undefined && userValues[b] !== undefined) {
      return -1
    } else if (userValues[b] === undefined && userValues[a] !== undefined) {
      return 1
    } else {
      return userValues[b] - userValues[a]
    }
  })

  return sortedUserValues
}

export const CompanyProfile: React.FC<CompanyProfileProps> = ({
  route,
}: CompanyProfileProps) => {
  const { theme } = useContext(ThemeContext)
  const { bgMain } = theme.colors
  const { auth, values } = useAppSelector(s => s)

  const [sortedUserValues, setSortedUserValues] = useState<string[]>(Object.keys(values?.map))
  const requestedId = String(route.params?.id || route.params?.company?._id || '')
  const [loading, setLoading] = useState(Boolean(requestedId))
  const [company, setCompany] = useState<Company | undefined>(undefined)
  const [showAlternatives, setShowAlternatives] = useState<boolean | undefined>(undefined)

  const [fetchingUserScores] = useState(false)
  const [errorState, setErrorState] = useState(false)

  const ASRef = useRef<CustomActionSheetRef>(null)
  const fetchRef = useRef(fetchingUserScores)

  const LOGO_SIZE = 150

  const closeActionSheet = () => {
    ASRef.current?.hide()
  }

  fetchRef.current = fetchingUserScores

  useEffect(() => {
    // TODO: this should be stored in redux to avoid calculating on each navigation
    if (auth.user && auth.user.values) {
      setSortedUserValues(s => sortUserValues(auth.user.values, s.slice()))
    }
  }, [auth])

  // const refetchCompany = useCallback((id?: string) => {
  //   if (!fetchRef.current) {
  //     console.log('fetching company with user scores...')
  //     setFetchingUserScores(true)
  //     // if route is nextjs, company._id should always be defined
  //     api.getCompanyInfo(id || company?._id).then(res => {
  //       setCompany(res.company)
  //     }).catch(ex => {
  //       log.error(ex)
  //       setErrorState(true)
  //     }).finally(() => {
  //       setFetchingUserScores(false)
  //       setLoading(false)
  //     })
  //   } else {
  //     console.log('refetch already in progress')
  //   }
  // }, [company?._id])

  // useEffect(() => {
  //   // console.log('s3 hook')
  //   // Scenario 3; SSR but need user scores OR user values changed
  //   if (auth.token && routeContext === RouterType.NextJS && company?._id) {
  //     // this is first load on a nextjs site, so we have to fetch user-specific scores
  //     refetchCompany()
  //   }
  // }, [auth.user.values, auth.token, routeContext, company?._id, refetchCompany])

  useEffect(() => {
    if (!requestedId) {
      setCompany(undefined)
      setShowAlternatives(undefined)
      setLoading(false)
      setErrorState(true)
      return
    }

    let cancelled = false
    setLoading(true)
    setCompany(undefined)
    setShowAlternatives(undefined)
    setErrorState(false)

    api.getCompanyInfo(requestedId).then(res => {
      if (cancelled) return
      setCompany(res.company)
      setShowAlternatives(res.showAlternatives)
      setLoading(false)
    }).catch(ex => {
      if (cancelled) return
      log.error(ex, { tags: { message: 'error fetching company info' } })
      setErrorState(true)
      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [requestedId])

  const authLoading = auth?.status === 'idle' || auth?.status === 'loading'

  if (loading || authLoading || !company) {
    return (
      <View style={{ flex: 1, backgroundColor: bgMain }}>
        <CompanyProfileSkeleton logoSize={LOGO_SIZE}/>
        <ThemedDialog
          visible={errorState}
          errorAnimation={errorState}
          title="Error"
          subMessage="An error occurred while retrieving company information. Please try again later."
          options={[
            {
              title: "Okay",
              role: "destructive",
              icon: <Ionicons name="checkmark" size={22} />,
              onPress: () => setErrorState(false),
            },
          ]}
        />
      </View>
    )
  }
  
  return (
    <View style={{flex: 1, backgroundColor: bgMain}}>
      <ScrollView
        style={{ flex: 1, marginBottom: 1 }}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.main_content, { maxWidth: MAX_CONTENT_WIDTH }]}
      >
        {/* <FocusEffect brand_name={name} /> */}
        {/* <Banner company={company} height={Platform.OS === 'web' ? 200 : undefined} /> */}
        <View style={{ flexDirection: 'row', marginBottom: 20, gap: 10, alignItems: 'flex-start', justifyContent: 'center', flexWrap: 'wrap'}}>
          <Logo
            company={company}
            style={{
              height: LOGO_SIZE,
              width: LOGO_SIZE,
              borderRadius: 16,
            }}
          />
          <Header company={company} values={values} />
        </View>

        <View style={{ marginBottom: 20 }}>
          <Leadership company={company} values={Object.values(values.map)} />
        </View>

        <View style={{ marginBottom: 20 }}>
          <Certifications company={company} values={Object.values(values.map)} />
        </View>

        <ProfileContent
          company={company}
          indicator={route.params?.indicator}
          sortedUserValues={sortedUserValues}
          fetchingUserScores={fetchingUserScores}
          showAlternatives={showAlternatives}
        />

        <DisclaimerAndFeedback company={company}/>

        <CustomActionSheet
          ref={ASRef}
          name="score-explanation"
        >
          <ScoreExplanationContent onClose={closeActionSheet} />
        </CustomActionSheet>
        {/* error modal */}
        <ThemedDialog
          visible={errorState}
          errorAnimation={errorState}
          title="Error"
          subMessage="An error occurred while retrieving company information. Please try again later."
          options={[
            {
              title: "Okay",
              role: "destructive",
              icon: <Ionicons name="checkmark" size={22} />,
              onPress: () => setErrorState(false),
            },
          ]}
        />
      </ScrollView>
    </View>
    
  )
}

const styles = StyleSheet.create({
  disclaimer: {
    fontSize: 12,
    paddingVertical: 10,
  },
  main_content: {
    alignSelf: 'center',
    width: "100%",
    padding: 10,
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  },
  bannerContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    padding: 10
  }
})

export default CompanyProfile

/**
 * react-native-web + SSR massively sucks at responsive design. To avoid shitty
 * initial layout and terribad flicker, just use css and media queries
 */
export const WebLayout = (props: CompanyProfileProps): React.ReactElement => {
  return <div className='company-profile-layout'>
    <div className='company-profile-main'>
      <CompanyProfile {...props} />
    </div>
  </div>
}

const DisclaimerAndFeedback = ({company}:{company: Company}) => {
  const theme = useContext(ThemeContext).theme
  const { primary, textAlt } = theme.colors

  const feedbackFormRef = useRef<CustomActionSheetRef>(null)

  const handleOpenFeedbackForm = () => {
    Haptics.impactLight()
    feedbackFormRef.current?.show()
  }

  const handleCloseFeedbackForm = () => {
    feedbackFormRef.current?.hide()
  }

  return <>
    <Text style={[textStyles.body, {color: textAlt, paddingVertical: 10}]}>
      OneEthos receives brand performance data from third party data
      sources. Some brand performance data may be derived from parent
      company.
    </Text>
    <Text style={[textStyles.body, {color: textAlt, paddingVertical: 10}]}>
      Notice something off? Tap
      <Text onPress={handleOpenFeedbackForm} style={{color: primary}}>{' '}here{' '}</Text>
      to share your feedback with us!
    </Text>
    <CustomActionSheet
      ref={feedbackFormRef}
      name="company-data-feedback"
    >
      <UserCompanyFeedbackForm company={company} closeSheet={handleCloseFeedbackForm} />
    </CustomActionSheet>
  </>
}
